<template>
  <div
    class="app-stat"
    :class="alignClass"
  >
    <div class="app-stat-icon">
      <slot name="icon">
        <app-icon
          variant="solid"
          :name="icon"
          :color="color"
        />
      </slot>
    </div>
    <div>
      <div class="app-stat-title">
        {{ title }}
      </div>
      <div :class="['app-stat-value', valueSizeClass]">
        <template v-if="loading">
          <div class="skeleton h-6 mt-1 min-w-18" />
        </template>
        <template v-else>
          {{ value }}
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { semanticColors } from '@/constants/colors';

defineOptions({
  name: 'TheStat',
});

const props = defineProps({
  icon: {
    type: String,
    default: 'info-circle',
  },
  title: {
    type: String,
    required: true,
  },
  value: {
    type: [Number, String],
    required: true,
  },
  align: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value),
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md'].includes(value),
  },
  color: {
    type: String,
    default: 'neutral',
    validator: (value) => semanticColors.includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const alignClass = computed(() => {
  return {
    'app-stat-align-right': props.align === 'right',
  };
});

const valueSizeClass = computed(() => {
  return {
    'app-stat-value-sm': props.size === 'sm',
  };
});
</script>

<style>
@reference '@/assets/main.css';

@layer components {
  .app-stat {
    @apply flex gap-4;

    &.app-stat-align-right {
      @apply text-right;
    }
  }

  .app-stat-icon {
    @apply flex items-center justify-center size-8 rounded-full;
  }

  .app-stat-title {
    @apply text-sm font-medium text-gray-600;
  }

  .app-stat-value {
    @apply text-xl font-semibold;

    &.app-stat-value-sm {
      @apply text-lg;
    }
  }
}
</style>
